import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";

import * as Yup from "yup";
import { Formik } from "formik";

import {
  Button,
  CircularProgress,
  Container,
  Grid,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";

import LineChart from "../../components/charts/LineChart";
import GenericPicker from "../../components/utilities/GenericPicker";
import DatePickerComponent from "../../components/utilities/DatePickerComponent";
import { getData } from "../../components/utilities/UtilityFunctions";

import { AuthContext } from "../../components/user/BCPAuthProvider";

const frequencyTypes = ["Monthly", "Weekly"];

function a11yProps(index) {
  return {
    id: `view-tab-${index}`,
  };
}

const processChartData = (data, filterState) => {
  var dataSet = Object.keys(data?.data)
    .map((key) => {
      return {
        date: key,
        value: data.data[key],
      };
    })
    .sort(function (a, b) {
      return new Date(a.date) - new Date(b.date);
    });
  var returnObj = {
    ...data,
    labels: dataSet.map((el) =>
      moment(el.date).format(
        filterState?.frequency === "monthly" ? "MMM-YY" : "YYYY-MM-DD"
      )
    ),
    dataset: dataSet.map((el) => el.value),
    cumulativeDataset: [],
  };
  let cumulative = data.precedingValue || 0;
  for (let item of returnObj.dataset) {
    cumulative += item;
    returnObj.cumulativeDataset.push(cumulative);
  }

  var a = returnObj.dataset[dataSet.length - 1] || 0;
  var b = returnObj.dataset[dataSet.length - 2] || 0;
  var c = returnObj.dataset[dataSet.length - 3] || 0;

  returnObj.growthCurrent =
    b === 0 && a !== 0 ? 100 : b === 0 ? 0 : ((a - b) / b) * 100;
  returnObj.growthPrevious =
    c === 0 && b !== 0 ? 100 : c === 0 ? 0 : ((b - c) / c) * 100;

  return returnObj;
};

const StatsBox = (props) => {
  const { label, data, view } = props;

  return (
    <Grid item xs={12} sm={6}>
      <Grid
        container
        sx={{
          border: "1px solid rgba(0, 0, 0, 0.12)",
          borderRadius: 1,
          boxShadow: 1,
        }}
      >
        <Grid
          item
          xs={8}
          textAlign="center"
          sx={{
            p: 2,
          }}
        >
          <Typography variant="h5">
            {label}
            {data ? (
              <Typography
                display="inline"
                sx={{
                  ml: "4px",
                  color:
                    data.growthCurrent > data.growthPrevious
                      ? "success.main"
                      : data.growthCurrent > 0 &&
                        data.growthCurrent < data.growthPrevious
                      ? "warning.main"
                      : data.growthCurrent < data.growthPrevious
                      ? "error.main"
                      : null,
                }}
              >{`${
                data.growthCurrent >= 0 ? "+" : ""
              }${data.growthCurrent.toFixed(2)}%`}</Typography>
            ) : (
              <CircularProgress
                sx={{
                  ml: "4px",
                }}
                size={12}
              />
            )}
          </Typography>
        </Grid>
        <Grid
          item
          xs={4}
          textAlign="center"
          sx={{
            p: 2,
            backgroundColor: `info.main`,
            color: `info.contrastText`,
          }}
        >
          <Typography variant="h4">
            {data ? (
              view === 0 ? (
                data.current.toLocaleString()
              ) : (
                data.total.toLocaleString()
              )
            ) : (
              <CircularProgress size={18} />
            )}
          </Typography>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default function AdminDashboard(props) {
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();

  const [filterState, setFilterState] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [view, setView] = useState(0);

  const [userData, setUserData] = useState(null);

  const [eventData, setEventData] = useState(null);

  const [playersData, setPlayersData] = useState(null);
  const [uniquePlayersData, setUniquePlayersData] = useState(null);

  const fetchUserData = async () => {
    setUserData(null);
    var returnData;
    var queryStringParameters = {
      ...filterState,
      reportType: "users",
      includePreceding: true,
    };
    try {
      returnData = await getData("/admin/dashboard", {
        queryStringParameters: queryStringParameters,
      });
    } catch (err) {
      console.error(err);
    }
    setUserData(processChartData(returnData, queryStringParameters));
    return true;
  };

  const fetchEventsData = async () => {
    setEventData(null);
    var returnData;
    var queryStringParameters = {
      ...filterState,
      reportType: "events",
      includePreceding: true,
    };
    try {
      returnData = await getData("/admin/dashboard", {
        queryStringParameters: queryStringParameters,
      });
    } catch (err) {
      console.error(err);
    }
    setEventData(processChartData(returnData, queryStringParameters));
    return true;
  };

  const fetchPlayersData = async () => {
    setPlayersData(null);
    var returnData;
    var queryStringParameters = {
      ...filterState,
      reportType: "players",
      includePreceding: true,
    };
    try {
      returnData = await getData("/admin/dashboard", {
        queryStringParameters: queryStringParameters,
      });
    } catch (err) {
      console.error(err);
    }
    setPlayersData(processChartData(returnData, queryStringParameters));
    return true;
  };

  const fetchUniquePlayersData = async () => {
    setUniquePlayersData(null);
    var returnData;
    var queryStringParameters = {
      ...filterState,
      reportType: "players",
      includePreceding: true,
      distinct: true,
    };
    try {
      returnData = await getData("/admin/dashboard", {
        queryStringParameters: queryStringParameters,
      });
    } catch (err) {
      console.error(err);
    }
    setUniquePlayersData(processChartData(returnData, queryStringParameters));
    return true;
  };

  async function parallel() {
    const taskUsers = fetchUserData();
    const taskEvents = fetchEventsData();

    const taskPlayersData = fetchPlayersData();
    const taskUniquePlayersData = fetchUniquePlayersData();

    return {
      users: await taskUsers,

      events: await taskEvents,

      players: await taskPlayersData,
      uniquePlayers: await taskUniquePlayersData,
    };
  }

  useEffect(() => {
    if (!authContext?.authState?.identity?.isAdmin) {
      navigate("/admin/settings?active_tab=game-systems");
      return;
    } // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const check = async () => {
      setProcessing(true);
      await parallel();
      setProcessing(false);
    };
    if (filterState) check();
    // eslint-disable-next-line
  }, [filterState]);

  return (
    <Container sx={{ pt: 2, pb: 2 }} maxWidth="lg">
      <Grid container sx={{ gap: 2 }}>
        <Grid
          item
          xs={12}
          sx={{
            p: 2,
            borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
          }}
        >
          <Typography variant="h3">Admin Dashboard</Typography>
        </Grid>

        <Grid
          item
          xs={12}
          sx={{
            p: 2,
            borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
          }}
        >
          <Formik
            initialValues={{
              startOfWeek: 1,
              startDate: moment()
                .subtract(3, "months")
                .startOf("month")
                .format("YYYY-MM-DD"),
              endDate: moment().format("YYYY-MM-DD"),
              frequency: "Monthly",
            }}
            enableReinitialize={true}
            validationSchema={Yup.object().shape({
              startDate: Yup.string().required(),
              endDate: Yup.string().required(),
            })}
            onSubmit={async (values) => {
              let sanitizedValues = {
                ...values,
                frequency: values.frequency.toLowerCase(),
              };
              if (values.startDate !== undefined && values.startDate !== "") {
                sanitizedValues.startDate = moment(values.startDate).format(
                  "YYYY-MM-DD"
                );
              }
              if (values.endDate !== undefined && values.endDate !== "") {
                sanitizedValues.endDate = moment(values.endDate).format(
                  "YYYY-MM-DD"
                );
              }
              if (sanitizedValues.frequency !== "weekly")
                delete sanitizedValues.startOfWeek;
              setFilterState(sanitizedValues);
            }}
          >
            {({ handleChange, handleSubmit, values, setFieldValue }) => (
              <form onSubmit={handleSubmit}>
                <Grid
                  container
                  spacing={2}
                  sx={{
                    pb: 1,
                    mb: 2,
                  }}
                >
                  <Grid item xs={12}>
                    <GenericPicker
                      label="Frequency"
                      name="frequency"
                      required
                      inclAny={false}
                      list={frequencyTypes}
                      value={values.frequency}
                      handleChange={handleChange}
                    />
                  </Grid>

                  {values.frequency === "Weekly" && (
                    <Grid item xs={12}>
                      <GenericPicker
                        label="Week Start"
                        name="startOfWeek"
                        required
                        inclAny={false}
                        list={[
                          { label: "Monday", value: 1 },
                          { label: "Tuesday", value: 2 },
                          { label: "Wednesday", value: 3 },
                          { label: "Thursday", value: 4 },
                          { label: "Friday", value: 5 },
                          { label: "Saturday", value: 6 },
                          { label: "Sunday", value: 0 },
                        ]}
                        value={values.startOfWeek}
                        handleChange={handleChange}
                      />
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <DatePickerComponent
                      name="startDate"
                      label="Start Date"
                      value={values.startDate}
                      setFieldValue={setFieldValue}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <DatePickerComponent
                      name="endDate"
                      label="End Date"
                      value={values.endDate}
                      setFieldValue={setFieldValue}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      color={"primary"}
                      variant={"contained"}
                      fullWidth
                      disabled={processing}
                    >
                      {filterState ? "Refresh" : "Run"}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </Grid>

        {filterState && (
          <>
            <Grid
              item
              xs={12}
              sx={{
                p: 2,
                borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
              }}
            >
              <Tabs
                value={view}
                onChange={(e, newValue) => {
                  setView(newValue);
                }}
                fullWidth
                variant="fullWidth"
                scrollButtons="auto"
                allowScrollButtonsMobile
                aria-label="Dashboard View"
              >
                <Tab
                  label={`Current${
                    filterState.frequency === "monthly"
                      ? " Month"
                      : filterState.frequency === "weekly"
                      ? " Week"
                      : ""
                  }`}
                  {...a11yProps(0)}
                />
                <Tab label="Cumulative" {...a11yProps(1)} />
              </Tabs>
            </Grid>
            <Grid
              item
              xs={12}
              sx={{
                p: 2,
                borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
              }}
            >
              <Grid container spacing={2}>
                <StatsBox label="Total Users" data={userData} view={view} />
                <StatsBox label="Total Events" data={eventData} view={view} />
                <StatsBox
                  label="Total Players"
                  data={playersData}
                  view={view}
                />
                <StatsBox
                  label="Unique Players"
                  data={uniquePlayersData}
                  view={view}
                />
              </Grid>
            </Grid>

            <Grid
              item
              xs={12}
              sx={{
                p: 2,
                borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
              }}
            >
              {userData && eventData && playersData && uniquePlayersData && (
                <LineChart
                  labels={userData.labels}
                  datasets={[
                    {
                      label: "Users",
                      data:
                        view === 0
                          ? userData.dataset
                          : userData.cumulativeDataset,
                    },
                    {
                      label: "Events",
                      data:
                        view === 0
                          ? eventData.dataset
                          : eventData.cumulativeDataset,
                    },
                    {
                      label: "Players",
                      data:
                        view === 0
                          ? playersData.dataset
                          : playersData.cumulativeDataset,
                    },
                    {
                      label: "Unique Players",
                      data:
                        view === 0
                          ? uniquePlayersData.dataset
                          : uniquePlayersData.cumulativeDataset,
                    },
                  ]}
                />
              )}
            </Grid>
          </>
        )}
      </Grid>
    </Container>
  );
}
